import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { StoreId, WebhookEndpointId } from "@KassaroAB/shared/types/ids";
import type { RestUpdateWebhookEndpoint } from "@KassaroAB/shared/types/webhookEndpoint";
import {
  createWebhookEndpointApi,
  deleteWebhookEndpointApi,
  getWebhookEndpointsApi,
  rotateWebhookSecretApi,
  sendWebhookTestApi,
  updateWebhookEndpointApi,
} from "../api/webhookEndpointsApi";

const webhookEndpointsKey = (storeId: StoreId | null) => [
  "webhookEndpoints",
  storeId,
];

export const useGetWebhookEndpointsQuery = (storeId: StoreId | null) =>
  useQuery({
    queryKey: webhookEndpointsKey(storeId),
    queryFn: () => getWebhookEndpointsApi(storeId!),
    enabled: !!storeId,
  });

const useWebhookEndpointMutation = <T, R>(
  storeId: StoreId | null,
  mutationFn: (storeId: StoreId, variables: T) => Promise<R>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: T) => mutationFn(storeId!, variables),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: webhookEndpointsKey(storeId) }),
  });
};

export const useCreateWebhookEndpointMutation = (storeId: StoreId | null) =>
  useWebhookEndpointMutation(storeId, createWebhookEndpointApi);

export const useUpdateWebhookEndpointMutation = (storeId: StoreId | null) =>
  useWebhookEndpointMutation(
    storeId,
    (
      id,
      {
        endpointId,
        data,
      }: { endpointId: WebhookEndpointId; data: RestUpdateWebhookEndpoint },
    ) => updateWebhookEndpointApi(id, endpointId, data),
  );

export const useDeleteWebhookEndpointMutation = (storeId: StoreId | null) =>
  useWebhookEndpointMutation(storeId, deleteWebhookEndpointApi);

export const useRotateWebhookSecretMutation = (storeId: StoreId | null) =>
  useWebhookEndpointMutation(storeId, rotateWebhookSecretApi);

export const useSendWebhookTestMutation = (storeId: StoreId | null) =>
  useWebhookEndpointMutation(storeId, sendWebhookTestApi);
